import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Pencil, ExternalLink } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Loading } from "@/components/common/Loading";
import { fileUrl } from "@/lib/api";

/** Lightweight, owner-view summary of the logged-in user's PERSONAL profile for the
 *  Chatter page left column. Always reflects the personal profile — it never changes
 *  when the composer identity switches to a business/capital profile. */
export function ChatterProfileCard({ profile, variant = "full" }) {
  if (profile === null || profile === undefined) {
    return variant === "slim"
      ? null
      : <Card className="border-0 p-6" data-testid="chatter-my-profile-card"><Loading /></Card>;
  }
  const p = profile;
  const loc = [p.location?.city, p.location?.state].filter(Boolean).join(", ");

  if (variant === "slim") {
    return (
      <Card className="border-0 p-3" data-testid="chatter-my-profile-card-slim">
        <div className="flex items-center gap-3">
          <Avatar className="h-10 w-10 shrink-0">
            {p.headshot_url && <AvatarImage src={fileUrl(p.headshot_url)} alt={p.display_name} className="object-cover" />}
            <AvatarFallback className="bg-teal-50 text-sm text-teal-700">{(p.display_name || "U")[0]}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            {p.slug ? (
              <Link to={`/p/${p.slug}`} className="block truncate text-sm font-[650] text-foreground hover:text-primary hover:underline" data-testid="chatter-my-profile-name">{p.display_name}</Link>
            ) : (
              <div className="truncate text-sm font-[650] text-foreground" data-testid="chatter-my-profile-name">{p.display_name}</div>
            )}
            <div className="truncate text-xs text-muted-foreground">
              {p.handle ? `@${p.handle}` : ""}{p.handle && loc ? " · " : ""}{loc}
            </div>
          </div>
          <Link to="/dashboard/profile" data-testid="chatter-my-profile-edit" className="shrink-0">
            <Button variant="secondary" size="sm"><Pencil className="mr-1.5 h-4 w-4" /> Edit</Button>
          </Link>
        </div>
      </Card>
    );
  }

  return (
    <Card className="border-0 p-6" data-testid="chatter-my-profile-card">
      <div className="flex flex-col items-center text-center">
        <Avatar className="h-20 w-20 border-4 border-card shadow-sm">
          {p.headshot_url && <AvatarImage src={fileUrl(p.headshot_url)} alt={p.display_name} className="object-cover" />}
          <AvatarFallback className="bg-teal-50 text-xl text-teal-700">{(p.display_name || "U")[0]}</AvatarFallback>
        </Avatar>
        <h2 className="mt-3 text-lg font-[700] text-foreground break-words" data-testid="chatter-my-profile-name">
          {p.slug ? <Link to={`/p/${p.slug}`} className="hover:text-primary hover:underline">{p.display_name}</Link> : p.display_name}
        </h2>
        {p.handle && <Link to={`/@${p.handle}`} className="text-sm font-[500] text-primary hover:underline" data-testid="chatter-my-profile-handle">@{p.handle}</Link>}
        {p.headline && <p className="mt-1.5 text-sm text-foreground">{p.headline}</p>}
        {loc && <div className="mt-1.5 flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="h-3.5 w-3.5" /> {loc}</div>}
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2">
        {p.slug ? (
          <Link to={`/p/${p.slug}`} data-testid="chatter-my-profile-view">
            <Button variant="secondary" size="sm" className="w-full justify-center"><ExternalLink className="mr-1.5 h-4 w-4" /> View</Button>
          </Link>
        ) : <span />}
        <Link to="/dashboard/profile" data-testid="chatter-my-profile-edit">
          <Button variant="secondary" size="sm" className="w-full justify-center"><Pencil className="mr-1.5 h-4 w-4" /> Edit</Button>
        </Link>
      </div>
    </Card>
  );
}

export default ChatterProfileCard;
